import React, { useState, useEffect, useCallback } from 'react';
import { Gift, ArrowRight, Sparkles, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useModalQueue } from '@/contexts/ModalQueueContext';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api-client';
import { storage } from '@/lib/storage';

interface PopupPromotion {
  id: number;
  title: string;
  subtitle?: string;
  short_description?: string;
  description: string;
  card_image_url?: string;
  slug?: string;
  cta_text?: string;
  cta_action_type?: string;
  cta_action_target?: string;
  promotion_layout_type_name?: string;
  badge_name?: string;
  badge_color?: string;
}

interface PromotionPopupProps {
  onClose: () => void;
  promotion: PopupPromotion;
}

const DISMISSED_POPUPS_KEY = 'dismissed_promotion_popups';
const POPUP_DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

function getDismissedPopups(): Record<number, number> {
  try {
    const stored = storage.getItem(DISMISSED_POPUPS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return {};
}

function setDismissedPopup(promotionId: number): void {
  const dismissed = getDismissedPopups();
  dismissed[promotionId] = Date.now();
  storage.setItem(DISMISSED_POPUPS_KEY, JSON.stringify(dismissed));
}

function isPopupDismissed(promotionId: number): boolean {
  const dismissed = getDismissedPopups();
  const dismissedAt = dismissed[promotionId];
  if (!dismissedAt) return false;
  return Date.now() - dismissedAt < POPUP_DISMISS_DURATION_MS;
}

export function PromotionPopup({ onClose, promotion }: PromotionPopupProps) {
  const navigate = useNavigate();
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowAnimation(true), 50);
  }, []);

  const handleClose = () => {
    setDismissedPopup(promotion.id);
    onClose();
  };

  const handleCTAClick = () => {
    const { cta_action_type, cta_action_target, slug } = promotion;

    if (cta_action_type === 'navigate' && cta_action_target) {
      onClose();
      navigate(cta_action_target);
    } else if (cta_action_type === 'external' && cta_action_target) {
      window.open(cta_action_target, '_blank', 'noopener,noreferrer');
      handleClose();
    } else if (slug) {
      onClose();
      navigate(`/promotions/${slug}`);
    } else {
      onClose();
      navigate('/promotions');
    }
  };

  const handleViewDetails = () => {
    if (promotion.slug) {
      onClose();
      navigate(`/promotions/${promotion.slug}`);
    } else {
      onClose();
      navigate('/promotions');
    }
  };

  const displayDescription = promotion.short_description || promotion.description;
  const truncatedDescription = displayDescription.length > 150 
    ? displayDescription.substring(0, 150) + '...' 
    : displayDescription;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4">
      {/* Backdrop with blur - no click to close */}
      <div 
        className={`absolute inset-0 bg-black/90 backdrop-blur-md transition-opacity duration-300 ${showAnimation ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* Modal Container */}
      <div 
        className={`relative bg-gradient-to-b from-[#1a1a2e] via-[#151525] to-[#0f0f1a] w-full max-w-sm rounded-2xl overflow-hidden border border-emerald-500/30 shadow-2xl shadow-emerald-500/30 transition-all duration-500 ${
          showAnimation ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        }`}
      >
        {/* Animated glow effect at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-40 bg-emerald-500/20 blur-3xl rounded-full animate-pulse pointer-events-none" />
        
        {/* Shimmer overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent pointer-events-none"
          style={{
            animation: 'shimmer 3s infinite',
            transform: 'translateX(-100%)',
          }}
        />

        {/* Image Section */}
        {promotion.card_image_url ? (
          <div className="relative w-full aspect-[16/9] overflow-hidden">
            <img 
              src={promotion.card_image_url} 
              alt={promotion.title}
              className="w-full h-full object-cover"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a2e] via-[#1a1a2e]/30 to-transparent" />
            
            {/* Badge with glow */}
            {promotion.badge_name && (
              <div className="absolute top-3 left-3 z-10">
                <div 
                  className="absolute inset-0 blur-lg rounded-full opacity-70"
                  style={{ backgroundColor: promotion.badge_color || '#10b981' }}
                />
                <div 
                  className="relative px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 shadow-lg"
                  style={{ 
                    backgroundColor: promotion.badge_color || '#10b981',
                    color: '#fff'
                  }}
                >
                  <Star className="h-3 w-3 fill-current" />
                  {promotion.badge_name}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Header with animated icon when no image */
          <div className="flex items-center justify-center pt-8 pb-4">
            <div className="relative">
              {/* Multi-layer glow effect */}
              <div className="absolute inset-0 bg-emerald-500/50 blur-3xl rounded-full animate-pulse" />
              <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 via-green-500/30 to-emerald-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
              <div className="absolute -inset-8 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
              
              {/* Icon container */}
              <div className="relative w-24 h-24 bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/50 ring-4 ring-emerald-400/20 ring-offset-4 ring-offset-[#1a1a2e]">
                <Gift className="h-12 w-12 text-white drop-shadow-lg" />
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="relative p-6 pt-4">
          <div className="text-center mb-5">
            {/* Subtitle pill */}
            {promotion.subtitle && (
              <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-emerald-500/20 via-green-500/15 to-emerald-500/20 text-emerald-300 text-[11px] font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-4 border border-emerald-500/20 backdrop-blur-sm">
                <Sparkles className="h-3.5 w-3.5 animate-pulse" />
                {promotion.subtitle}
              </div>
            )}
            
            {/* Title with shadow */}
            <h2 className="text-2xl font-black text-white mb-3 drop-shadow-[0_2px_10px_rgba(16,185,129,0.3)]">
              {promotion.title}
            </h2>
            
            {/* Description */}
            <p className="text-white/70 text-sm leading-relaxed">
              {truncatedDescription}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {/* Primary CTA with shimmer effect */}
            {promotion.cta_text && (
              <button
                onClick={handleCTAClick}
                className="relative w-full py-4 rounded-xl font-bold text-white overflow-hidden group"
              >
                {/* Button background */}
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-500 bg-[length:200%_100%] group-hover:bg-[position:100%_0] transition-all duration-500" />
                
                {/* Shimmer effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                
                {/* Shadow */}
                <div className="absolute inset-0 shadow-lg shadow-emerald-500/40" />
                
                {/* Content */}
                <span className="relative flex items-center justify-center gap-2">
                  {promotion.cta_text}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </button>
            )}

            {/* Secondary button */}
            <button
              onClick={handleViewDetails}
              className="w-full py-3.5 rounded-xl font-semibold text-emerald-400 hover:text-emerald-300 transition-all flex items-center justify-center gap-1.5 border border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-500/5 group"
            >
              View Details
              <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>

            {/* Dismiss button */}
            <button
              onClick={handleClose}
              className="w-full py-2.5 rounded-xl font-medium text-white/40 hover:text-white/60 transition-all text-sm"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>

      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

export function usePromotionPopup() {
  const { user } = useAuth();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  const [promotions, setPromotions] = useState<PopupPromotion[]>([]);
  const [currentPromotion, setCurrentPromotion] = useState<PopupPromotion | null>(null);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    if (hasChecked || !user?.email_verified) return;

    const fetchPopupPromotions = async () => {
      try {
        const response = await apiClient.get<{
          success: boolean;
          promotions: PopupPromotion[];
        }>('/promotions/customer/popups');

        if (response.success && response.promotions?.length > 0) {
          const nonDismissed = response.promotions.filter(p => !isPopupDismissed(p.id));
          setPromotions(nonDismissed);
          
          if (nonDismissed.length > 0) {
            setCurrentPromotion(nonDismissed[0]);
            requestShow('PROMOTION_POPUP');
          }
        }
      } catch {
        // Silently fail - popup promotions are not critical
      }
    };

    fetchPopupPromotions();
    setHasChecked(true);
  }, [user?.email_verified, hasChecked, requestShow]);

  const closePopup = useCallback(() => {
    if (currentPromotion) {
      setDismissedPopup(currentPromotion.id);
    }
    
    const remaining = promotions.filter(p => p.id !== currentPromotion?.id && !isPopupDismissed(p.id));
    
    if (remaining.length > 0) {
      setCurrentPromotion(remaining[0]);
    } else {
      setCurrentPromotion(null);
      notifyDismissed('PROMOTION_POPUP');
    }
  }, [currentPromotion, promotions, notifyDismissed]);

  return {
    showPopup: shouldShow('PROMOTION_POPUP') && currentPromotion !== null,
    currentPromotion,
    closePopup,
  };
}
